import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword, onAuthStateChanged, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { auth } from '../Firebase';
import { motion } from 'framer-motion';
import { KeyRound, Mail, ShieldAlert, Loader2, Lock, Eye, EyeOff } from 'lucide-react';

export default function AdminSignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();

  // If already authenticated, redirect to dashboard automatically
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate('/admin/dashboard');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Read and clear routing state messages (e.g. session timeout)
  useEffect(() => {
    if (location.state?.message) {
      setError(location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      // Set session persistence so that closing the tab logs the user out
      await setPersistence(auth, browserSessionPersistence);
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Sign-in error:', err);
      // Map standard Firebase Auth errors to human-friendly messages
      switch (err.code) {
        case 'auth/invalid-email':
          setError('Invalid email address format.');
          break;
        case 'auth/user-disabled':
          setError('This administrator account has been disabled.');
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setError('Incorrect credentials. Please verify your email and password.');
          break;
        default:
          setError('Failed to authenticate. Please check your network connection.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-sans bg-brand-navy min-h-screen flex items-center justify-center relative px-6 py-12 grid-mesh overflow-hidden">
      {/* Background Soft Glows */}
      <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-brand-blue/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 w-96 h-96 bg-brand-green/5 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="w-full max-w-md bg-brand-dark/40 backdrop-blur-lg border border-white/10 rounded-3xl p-8 sm:p-10 shadow-2xl relative z-10 space-y-8"
      >
        {/* Header Section */}
        <div className="text-center space-y-3">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-navy to-brand-blue flex items-center justify-center border border-white/10 shadow-xl shadow-brand-blue/5">
            <Lock className="text-brand-blue w-7 h-7" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold font-montserrat text-white tracking-tight">
            Kiran Administration
          </h2>
          <p className="text-xs sm:text-sm text-brand-silver/60 font-poppins">
            Access secure manager and metrics portal
          </p>
        </div>

        {/* Form Details */}
        <form onSubmit={handleSignIn} className="space-y-6">
          {error && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-4 bg-brand-blue/10 border-l-4 border-brand-blue text-brand-blue text-xs flex items-start gap-2.5 rounded-r-xl"
            >
              <ShieldAlert className="shrink-0 mt-0.5" size={16} />
              <p className="font-poppins font-medium leading-relaxed">{error}</p>
            </motion.div>
          )}

          {/* Email input field */}
          <div className="space-y-2">
            <label className="block text-xs font-bold uppercase tracking-widest text-brand-silver/70 font-poppins">
              Admin Email
            </label>
            <div className="relative">
              <input
                type="email"
                required
                placeholder="admin@kirandistributors.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-3.5 bg-white/5 border border-white/15 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-white/30 transition-all text-white font-poppins text-sm placeholder:text-brand-silver/30"
              />
              <Mail size={18} className="absolute left-4 top-4 text-brand-silver/40" />
            </div>
          </div>

          {/* Password input field */}
          <div className="space-y-2">
            <label className="block text-xs font-bold uppercase tracking-widest text-brand-silver/70 font-poppins">
              Access Code
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-11 pr-12 py-3.5 bg-white/5 border border-white/15 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-white/30 transition-all text-white font-poppins text-sm placeholder:text-brand-silver/30"
              />
              <KeyRound size={18} className="absolute left-4 top-4 text-brand-silver/40" />
              
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-4 text-brand-silver/40 hover:text-white transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary py-4 text-sm font-bold font-poppins justify-center shadow-lg shadow-brand-blue/10 flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Verifying Credentials...
              </>
            ) : (
              <>
                Sign In to Dashboard
              </>
            )}
          </button>
        </form>

        {/* Back Link */}
        <div className="text-center pt-2">
          <Link
            to="/contact"
            className="text-xs text-brand-silver/45 hover:text-brand-blue transition-colors font-poppins font-medium inline-block"
          >
            Need help? Settle coordinates with corporate desk
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
